import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import type {
  CustomerCreditRuntimeItem,
  CustomerOpenReceivableRuntimeItem,
} from "../runtime/gateway/local-projection-client";
import type {
  PaymentMethodRuntimeProjection,
  PaymentReceivingDestinationProjection,
} from "../runtime/gateway/payment-runtime-client";
import {
  PosSalesHistoryGatewayClient,
  type PosOrderDetailResponse,
} from "../runtime/gateway/sales-history-client";
import { readLocalCart, type LocalSaleMember } from "../runtime/local-store/cart-state";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { readLocalCustomerCredit } from "../runtime/local-store/indexed-runtime";
import { readPaymentRuntime, type LocalPaymentRuntime } from "../runtime/local-store/payment-runtime";
import {
  commitLocalReceivableSettlementAll,
  syncPendingReceivableSettlementAll,
  syncReceivableSettlementAllOutbox,
  type LocalReceivableSettlementAllSnapshot,
} from "../runtime/local-store/receivable-settlement-all-state";
import {
  commitLocalReceivableSettlement,
  syncPendingReceivableSettlements,
  syncReceivableSettlementOutbox,
  type LocalReceivableSettlementSnapshot,
} from "../runtime/local-store/receivable-settlement-state";
import {
  readSalesOrderDetail,
  saveSalesOrderDetail,
} from "../runtime/local-store/secondary-runtime-cache";
import { readLocalShift, type LocalShiftState } from "../runtime/local-store/shift-state";
import {
  readTerminalCredential,
  type TerminalCredentialState,
} from "../runtime/local-store/terminal-state";

type MemberReceivableSnapshot = {
  member: LocalSaleMember;
  credit: CustomerCreditRuntimeItem | null;
  terminal: TerminalCredentialState;
  session: LocalEmployeeSessionState | null;
  shift: LocalShiftState | null;
  paymentRuntime: LocalPaymentRuntime;
} | null;

type SettlementState = "IDLE" | "COMMITTING" | "SYNCING" | "PENDING" | "ERROR";

function money(value: number | null | undefined, currency = "THB") {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value));
}

function shortDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(`${value.length === 10 ? `${value}T00:00:00` : value}`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  }).format(date);
}

function shortTime(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function detailText(details: Record<string, unknown> | undefined, key: string) {
  const value = details?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function remainingOf(item: CustomerOpenReceivableRuntimeItem) {
  const remaining = Number(item.remaining_amount ?? item.original_amount);
  return Number.isFinite(remaining) ? Math.max(remaining, 0) : 0;
}

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function friendlySettlementError(value: string) {
  if (value.includes("RECEIVABLE_BATCH_CHANGED") || value.includes("RECEIVABLE_BATCH_TOTAL_MISMATCH")) {
    return "ยอดค้างมีการเปลี่ยนแปลง กรุณาตรวจรายการล่าสุดแล้วชำระทั้งหมดอีกครั้ง";
  }
  if (value.includes("RECEIVABLE_BATCH_EMPTY")) return "สมาชิกคนนี้ไม่มีรายการค้างชำระแล้ว";
  if (value.includes("RECEIVABLE_BATCH_CURRENCY_MISMATCH")) return "รายการค้างชำระมีสกุลเงินไม่ตรงกัน จึงยังชำระทั้งหมดไม่ได้";
  if (value.includes("RECEIVABLE_BATCH_INVALID") || value.includes("RECEIVABLE_BATCH_TOTAL_INVALID")) return "รายการชำระทั้งหมดไม่ถูกต้อง กรุณากลับไปตรวจรายการอีกครั้ง";
  if (value.includes("RECEIVABLE_ALREADY_PAID") || value.includes("RECEIVABLE_NOT_COLLECTIBLE")) return "รายการนี้ไม่มีค้างชำระแล้ว กรุณารอข้อมูลล่าสุดจากระบบ";
  if (value.includes("RECEIVABLE_OVERPAYMENT_NOT_ALLOWED")) return "ยอดรับชำระเกินยอดคงเหลือ กรุณาตรวจจำนวนเงินอีกครั้ง";
  if (value.includes("RECEIVABLE_PAYMENT_AMOUNT_INVALID")) return "กรุณาระบุยอดรับชำระให้ถูกต้อง";
  if (value.includes("ACTIVE_POS_SHIFT_REQUIRED")) return "กรุณาเปิดกะก่อนรับชำระ";
  if (value.includes("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์รับชำระ";
  if (value.includes("PAYMENT_METHOD_NOT_READY") || value.includes("PAYMENT_RECEIVING_ROUTE_NOT_READY")) return "ช่องทางรับเงินนี้ยังไม่พร้อมใช้งาน กรุณาตรวจการตั้งค่าร้าน";
  if (value.includes("PAYMENT_RECEIVING_DESTINATION_REQUIRED") || value.includes("PAYMENT_RECEIVING_DESTINATION_NOT_ELIGIBLE")) return "กรุณาเลือกช่องทางรับเงินที่ร้านอนุญาต";
  if (value.includes("TENDERED_AMOUNT_INVALID")) return "จำนวนเงินสดที่รับมาต้องไม่น้อยกว่ายอดชำระ";
  if (value.includes("LOCAL_CHANGE_AMOUNT_MISMATCH")) return "ยอดเงินทอนไม่ถูกต้อง กรุณาตรวจจำนวนเงินสดอีกครั้ง";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID") || value.includes("DEVICE_CREDENTIAL_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง";
  if (value.includes("REQUEST_KEY_CONFLICT")) return "คำขอเดิมไม่ตรงกับรายการนี้ กรุณาเริ่มรับชำระใหม่";
  if (
    value.includes("RECEIVABLE_COLLECTION_REQUIRES_ONLINE")
    || value.includes("GATEWAY_UNREACHABLE")
    || value.includes("GATEWAY_TIMEOUT")
    || value.includes("RECEIVABLE_SETTLEMENT_ALL_FAILED")
  ) return "ยังยืนยันการรับชำระกับระบบไม่ได้ ระบบเก็บคำขอเดิมไว้ให้ลองใหม่";
  return "รับชำระไม่สำเร็จ กรุณาลองอีกครั้ง";
}

function friendlyDetailError(value: string) {
  if (value.includes("COMMERCE_ORDER_NOT_FOUND")) return "ยังเปิดรายละเอียดบิลนี้จากสาขาปัจจุบันไม่ได้";
  if (value.includes("POS_SALES_HISTORY_PERMISSION_REQUIRED") || value.includes("PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์ดูรายละเอียดบิล";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID") || value.includes("DEVICE_CREDENTIAL_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง";
  if (value.includes("GATEWAY_UNREACHABLE") || value.includes("GATEWAY_TIMEOUT")) return "โหลดรายละเอียดล่าสุดไม่ได้ หากเคยเปิดบิลนี้มาก่อนระบบจะใช้ข้อมูลที่เก็บในเครื่อง";
  return "โหลดรายละเอียดบิลไม่สำเร็จ กรุณาลองอีกครั้ง";
}

function DestinationSummary({ destination }: { destination: PaymentReceivingDestinationProjection }) {
  const accountName = detailText(destination.public_details, "account_name");
  const accountIdentifier = detailText(destination.public_details, "account_identifier");
  const instructions = detailText(destination.public_details, "instructions");
  return (
    <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10, display: "grid", gap: 3 }}>
      <strong>{destination.display_name}</strong>
      {accountName ? <small>ชื่อผู้รับ: {accountName}</small> : null}
      {accountIdentifier ? <small>{accountIdentifier}</small> : null}
      {instructions ? <small>{instructions}</small> : null}
    </div>
  );
}

export function MemberReceivableHistoryHost() {
  const [snapshot, setSnapshot] = useState<MemberReceivableSnapshot>(null);
  const [barNode, setBarNode] = useState<Element | null>(null);
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" ? true : navigator.onLine);
  const [settling, setSettling] = useState<CustomerOpenReceivableRuntimeItem | null>(null);
  const [settlingAll, setSettlingAll] = useState(false);
  const [amountText, setAmountText] = useState("");
  const [tenderedText, setTenderedText] = useState("");
  const [selectedMethodCode, setSelectedMethodCode] = useState("");
  const [selectedDestinationId, setSelectedDestinationId] = useState("");
  const [settlementState, setSettlementState] = useState<SettlementState>("IDLE");
  const [settlementMessage, setSettlementMessage] = useState<string | null>(null);
  const [settlementIntent, setSettlementIntent] = useState<LocalReceivableSettlementSnapshot | null>(null);
  const [settlementAllIntent, setSettlementAllIntent] = useState<LocalReceivableSettlementAllSnapshot | null>(null);
  const [detailReceivable, setDetailReceivable] = useState<CustomerOpenReceivableRuntimeItem | null>(null);
  const [detail, setDetail] = useState<PosOrderDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    const resolveBar = () => setBarNode(document.querySelector(".sale-context-bar"));
    resolveBar();
    const observer = new MutationObserver(resolveBar);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateNetwork = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
    };
  }, []);

  useEffect(() => {
    const subscription = liveQuery(async (): Promise<MemberReceivableSnapshot> => {
      const terminal = await readTerminalCredential();
      const identity = terminal?.identity ?? null;
      if (!terminal || !identity) return null;
      const [cart, session, shift, paymentRuntime] = await Promise.all([
        readLocalCart(identity),
        readLocalEmployeeSession(),
        readLocalShift(),
        readPaymentRuntime(identity),
      ]);
      if (!cart.selectedMember) return null;
      const credit = await readLocalCustomerCredit(identity, cart.selectedMember.customerId);
      return { member: cart.selectedMember, credit, terminal, session, shift, paymentRuntime };
    }).subscribe({
      next: (next) => {
        setSnapshot(next);
        if (!next) {
          setOpen(false);
          setSettling(null);
          setSettlingAll(false);
          setDetailReceivable(null);
          setDetail(null);
        }
      },
      error: () => {
        setSnapshot(null);
        setOpen(false);
        setSettling(null);
        setSettlingAll(false);
        setDetailReceivable(null);
        setDetail(null);
      },
    });
    return () => subscription.unsubscribe();
  }, []);

  const terminalIdentity = snapshot?.terminal.identity ?? null;
  const sessionToken = snapshot?.session?.sessionToken ?? "";
  const deviceSecret = snapshot?.terminal.deviceSecret ?? "";
  useEffect(() => {
    if (!online || !terminalIdentity || !deviceSecret || !sessionToken) return;
    void Promise.all([
      syncPendingReceivableSettlements(terminalIdentity, deviceSecret, sessionToken),
      syncPendingReceivableSettlementAll(terminalIdentity, deviceSecret, sessionToken),
    ]);
  }, [deviceSecret, online, sessionToken, terminalIdentity?.branch_id, terminalIdentity?.node_id, terminalIdentity?.organization_id]);

  const credit = snapshot?.credit ?? null;
  const count = credit?.open_receivable_count ?? 0;
  const receivables = credit?.open_receivables ?? [];
  const hasOutstanding = Boolean(snapshot && credit && credit.current_outstanding > 0 && count > 0);
  const payAllTotal = useMemo(
    () => roundMoney(receivables.reduce((sum, item) => sum + remainingOf(item), 0)),
    [receivables],
  );
  const paymentMethods = useMemo(() => (snapshot?.paymentRuntime.payload?.payment_methods ?? []).filter((method) => (
    method.ready
      && !method.provider_required
      && (method.settlement_mode === "IMMEDIATE_CASH" || method.settlement_mode === "RECEIVING_DESTINATION")
  )), [snapshot?.paymentRuntime.payload]);
  const selectedMethod = paymentMethods.find((method) => method.payment_method_code === selectedMethodCode) ?? null;
  const destinations = selectedMethod?.settlement_mode === "RECEIVING_DESTINATION" && selectedMethod.primary_destination
    ? [selectedMethod.primary_destination, ...(selectedMethod.fallback_destinations ?? [])]
    : [];
  const selectedDestination = destinations.find((destination) => destination.destination_id === selectedDestinationId)
    ?? selectedMethod?.primary_destination
    ?? null;
  const amount = Number(amountText);
  const settlementAmount = settlingAll ? payAllTotal : amount;
  const tendered = Number(tenderedText);
  const isCash = selectedMethod?.settlement_mode === "IMMEDIATE_CASH";
  const localChange = isCash && Number.isFinite(settlementAmount) && Number.isFinite(tendered)
    ? Math.max(tendered - settlementAmount, 0)
    : 0;
  const actor = snapshot?.session?.actor ?? null;
  const shift = snapshot?.shift?.shift ?? null;
  const sameScope = Boolean(actor && terminalIdentity
    && actor.organization_id === terminalIdentity.organization_id
    && actor.branch_id === terminalIdentity.branch_id
    && actor.node_id === terminalIdentity.node_id
    && actor.runtime_session_status === "ACTIVE");
  const canCollect = Boolean(
    online
      && sameScope
      && actor?.permission_codes.includes("commerce.sale.commit")
      && shift?.shift_status === "OPEN"
      && paymentMethods.length > 0,
  );

  function resetSettlement() {
    setSettling(null);
    setSettlingAll(false);
    setAmountText("");
    setTenderedText("");
    setSelectedMethodCode("");
    setSelectedDestinationId("");
    setSettlementState("IDLE");
    setSettlementMessage(null);
    setSettlementIntent(null);
    setSettlementAllIntent(null);
  }

  function resetDetail() {
    setDetailReceivable(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError(null);
  }

  async function loadReceivableDetail(item: CustomerOpenReceivableRuntimeItem) {
    if (!snapshot || !terminalIdentity) return;
    setDetailReceivable(item);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const cached = await readSalesOrderDetail(terminalIdentity, item.source_order_id);
      if (cached) setDetail(cached);

      if (!online) {
        if (!cached) setDetailError("รายละเอียดบิลนี้ยังไม่ได้เก็บไว้ในเครื่อง เชื่อมต่ออินเทอร์เน็ตหนึ่งครั้งเพื่อโหลดรายละเอียด");
        return;
      }

      const baseUrl = getGatewayBaseUrl();
      if (!baseUrl || !snapshot.session?.sessionToken) {
        if (!cached) setDetailError("ยังเชื่อมต่อ MORE Core เพื่อโหลดรายละเอียดบิลไม่ได้");
        return;
      }
      const client = new PosSalesHistoryGatewayClient({ baseUrl });
      const response = await client.detail({
        deviceSecret: snapshot.terminal.deviceSecret,
        sessionToken: snapshot.session.sessionToken,
        orderId: item.source_order_id,
      });
      if (!response.ok || !response.data) throw new Error(response.error ?? "POS_ORDER_DETAIL_LOAD_FAILED");
      await saveSalesOrderDetail(terminalIdentity, response.data);
      setDetail(response.data);
    } catch (error) {
      setDetailError(friendlyDetailError(error instanceof Error ? error.message : "POS_ORDER_DETAIL_LOAD_FAILED"));
    } finally {
      setDetailLoading(false);
    }
  }

  function beginSettlement(item: CustomerOpenReceivableRuntimeItem) {
    const remaining = remainingOf(item);
    const initialMethod = paymentMethods.length === 1 ? paymentMethods[0] : null;
    resetDetail();
    setSettlingAll(false);
    setSettling(item);
    setAmountText(remaining.toFixed(2));
    setSelectedMethodCode(initialMethod?.payment_method_code ?? "");
    setSelectedDestinationId(initialMethod?.primary_destination?.destination_id ?? "");
    setTenderedText(initialMethod?.settlement_mode === "IMMEDIATE_CASH" ? remaining.toFixed(2) : "");
    setSettlementState("IDLE");
    setSettlementMessage(null);
    setSettlementIntent(null);
    setSettlementAllIntent(null);
  }

  function beginSettlementAll() {
    if (!receivables.length || payAllTotal <= 0) return;
    const initialMethod = paymentMethods.length === 1 ? paymentMethods[0] : null;
    resetDetail();
    setSettling(null);
    setSettlingAll(true);
    setAmountText("");
    setSelectedMethodCode(initialMethod?.payment_method_code ?? "");
    setSelectedDestinationId(initialMethod?.primary_destination?.destination_id ?? "");
    setTenderedText(initialMethod?.settlement_mode === "IMMEDIATE_CASH" ? payAllTotal.toFixed(2) : "");
    setSettlementState("IDLE");
    setSettlementMessage(null);
    setSettlementIntent(null);
    setSettlementAllIntent(null);
  }

  function selectMethod(method: PaymentMethodRuntimeProjection) {
    setSelectedMethodCode(method.payment_method_code);
    setSelectedDestinationId(method.primary_destination?.destination_id ?? "");
    setTenderedText(
      method.settlement_mode === "IMMEDIATE_CASH" && Number.isFinite(settlementAmount)
        ? settlementAmount.toFixed(2)
        : "",
    );
    setSettlementMessage(null);
  }

  async function confirmSettlement() {
    if (!snapshot || !terminalIdentity || !actor || !shift || !settling || !selectedMethod || !canCollect) return;
    const remaining = remainingOf(settling);
    if (!Number.isFinite(amount) || amount <= 0 || amount > remaining + 0.005) {
      setSettlementState("ERROR");
      setSettlementMessage(amount > remaining ? "ยอดรับชำระเกินยอดคงเหลือ" : "กรุณาระบุยอดรับชำระให้ถูกต้อง");
      return;
    }
    if (isCash && (!Number.isFinite(tendered) || tendered < amount)) {
      setSettlementState("ERROR");
      setSettlementMessage("จำนวนเงินสดที่รับมาต้องไม่น้อยกว่ายอดชำระ");
      return;
    }
    if (!isCash && !selectedDestination) {
      setSettlementState("ERROR");
      setSettlementMessage("กรุณาเลือกช่องทางรับเงิน");
      return;
    }

    try {
      setSettlementState("COMMITTING");
      setSettlementMessage(null);
      const intent = await commitLocalReceivableSettlement({
        identity: terminalIdentity,
        actor,
        shift,
        receivableId: settling.receivable_id,
        customerId: snapshot.member.customerId,
        remainingAmount: remaining,
        amount,
        currencyCode: settling.currency_code,
        paymentMethodCode: selectedMethod.payment_method_code,
        settlementMode: selectedMethod.settlement_mode,
        receivingDestinationId: isCash ? null : selectedDestination?.destination_id ?? null,
        receivingDestinationName: isCash ? null : selectedDestination?.display_name ?? null,
        tenderedAmount: isCash ? tendered : null,
        localChangeAmount: isCash ? localChange : null,
      });
      setSettlementIntent(intent);
      setSettlementState("SYNCING");
      const confirmed = await syncReceivableSettlementOutbox(
        terminalIdentity,
        snapshot.terminal.deviceSecret,
        snapshot.session!.sessionToken,
        intent.rootRequestKey,
      );
      const remainingAfter = confirmed.canonical?.receivable_balance.remaining_amount ?? Math.max(remaining - amount, 0);
      setSettlementMessage(remainingAfter > 0.005
        ? `รับชำระสำเร็จ เหลือค้าง ${money(remainingAfter, settling.currency_code)}`
        : "รับชำระครบแล้ว");
      setSettlementState("IDLE");
      setSettlementIntent(null);
      setSettling(null);
    } catch (error) {
      const code = error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_FAILED";
      const pending = code.includes("GATEWAY_UNREACHABLE") || code.includes("GATEWAY_TIMEOUT") || code.includes("RECEIVABLE_SETTLEMENT_FAILED");
      setSettlementState(pending ? "PENDING" : "ERROR");
      setSettlementMessage(friendlySettlementError(code));
      if (!pending) setSettlementIntent(null);
    }
  }

  async function confirmSettlementAll() {
    if (!snapshot || !terminalIdentity || !actor || !shift || !settlingAll || !selectedMethod || !canCollect) return;
    if (!receivables.length || !Number.isFinite(payAllTotal) || payAllTotal <= 0) {
      setSettlementState("ERROR");
      setSettlementMessage("สมาชิกคนนี้ไม่มีรายการค้างชำระแล้ว");
      return;
    }
    if (isCash && (!Number.isFinite(tendered) || tendered < payAllTotal)) {
      setSettlementState("ERROR");
      setSettlementMessage("จำนวนเงินสดที่รับมาต้องไม่น้อยกว่ายอดชำระ");
      return;
    }
    if (!isCash && !selectedDestination) {
      setSettlementState("ERROR");
      setSettlementMessage("กรุณาเลือกช่องทางรับเงิน");
      return;
    }

    try {
      setSettlementState("COMMITTING");
      setSettlementMessage(null);
      const intent = await commitLocalReceivableSettlementAll({
        identity: terminalIdentity,
        actor,
        shift,
        customerId: snapshot.member.customerId,
        receivables,
        paymentMethodCode: selectedMethod.payment_method_code,
        settlementMode: selectedMethod.settlement_mode,
        receivingDestinationId: isCash ? null : selectedDestination?.destination_id ?? null,
        receivingDestinationName: isCash ? null : selectedDestination?.display_name ?? null,
        tenderedAmount: isCash ? tendered : null,
        localChangeAmount: isCash ? localChange : null,
      });
      setSettlementAllIntent(intent);
      setSettlementState("SYNCING");
      const confirmed = await syncReceivableSettlementAllOutbox(
        terminalIdentity,
        snapshot.terminal.deviceSecret,
        snapshot.session!.sessionToken,
        intent.rootRequestKey,
      );
      const paidCount = confirmed.canonical?.receivable_count ?? intent.receivables.length;
      setSettlementMessage(`ชำระทั้งหมดสำเร็จ ${paidCount} บิล`);
      setSettlementState("IDLE");
      setSettlementAllIntent(null);
      setSettlingAll(false);
    } catch (error) {
      const code = error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_ALL_FAILED";
      const pending = code.includes("GATEWAY_UNREACHABLE") || code.includes("GATEWAY_TIMEOUT") || code.includes("RECEIVABLE_SETTLEMENT_ALL_FAILED");
      setSettlementState(pending ? "PENDING" : "ERROR");
      setSettlementMessage(friendlySettlementError(code));
      if (!pending) setSettlementAllIntent(null);
    }
  }

  async function retrySettlement() {
    if (!snapshot || !terminalIdentity || !settlementIntent || !online) return;
    try {
      setSettlementState("SYNCING");
      setSettlementMessage("กำลังตรวจสอบคำขอเดิม…");
      const confirmed = await syncReceivableSettlementOutbox(
        terminalIdentity,
        snapshot.terminal.deviceSecret,
        snapshot.session!.sessionToken,
        settlementIntent.rootRequestKey,
      );
      const remainingAfter = confirmed.canonical?.receivable_balance.remaining_amount ?? 0;
      setSettlementMessage(remainingAfter > 0.005
        ? `รับชำระสำเร็จ เหลือค้าง ${money(remainingAfter, settling?.currency_code ?? credit?.currency_code ?? "THB")}`
        : "รับชำระครบแล้ว");
      setSettlementState("IDLE");
      setSettlementIntent(null);
      setSettling(null);
    } catch (error) {
      const code = error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_FAILED";
      setSettlementState("PENDING");
      setSettlementMessage(friendlySettlementError(code));
    }
  }

  async function retrySettlementAll() {
    if (!snapshot || !terminalIdentity || !settlementAllIntent || !online) return;
    try {
      setSettlementState("SYNCING");
      setSettlementMessage("กำลังตรวจสอบคำขอเดิม…");
      const confirmed = await syncReceivableSettlementAllOutbox(
        terminalIdentity,
        snapshot.terminal.deviceSecret,
        snapshot.session!.sessionToken,
        settlementAllIntent.rootRequestKey,
      );
      const paidCount = confirmed.canonical?.receivable_count ?? settlementAllIntent.receivables.length;
      setSettlementMessage(`ชำระทั้งหมดสำเร็จ ${paidCount} บิล`);
      setSettlementState("IDLE");
      setSettlementAllIntent(null);
      setSettlingAll(false);
    } catch (error) {
      const code = error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_ALL_FAILED";
      setSettlementState("PENDING");
      setSettlementMessage(friendlySettlementError(code));
    }
  }

  return (
    <>
      {barNode && hasOutstanding ? createPortal(
        <button
          type="button"
          className="sale-context-chip sale-context-chip--active"
          onClick={() => {
            setOpen(true);
            resetDetail();
            setSettlementMessage(null);
          }}
          title="ดูรายการค้างชำระของสมาชิกคนนี้"
        >
          <span aria-hidden="true">฿</span>
          <b>ค้าง {money(credit!.current_outstanding, credit!.currency_code)} · {count} บิล</b>
        </button>,
        barNode,
      ) : null}

      {open && snapshot && credit ? createPortal(
        <div className="sale-context-backdrop" role="presentation">
          <section className="sale-context-modal" role="dialog" aria-modal="true" aria-labelledby="member-receivable-title">
            <header>
              <div><h2 id="member-receivable-title">รายการค้างชำระ</h2></div>
              <button type="button" onClick={() => { resetSettlement(); resetDetail(); setOpen(false); }} aria-label="ปิด">×</button>
            </header>

            <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(3, minmax(0, 1fr))", marginBottom: 12 }}>
              <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10 }}>
                <small>สมาชิก</small><div><strong>{snapshot.member.customerName}</strong></div>
              </div>
              <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10 }}>
                <small>ยอดค้างปัจจุบัน</small><div><strong>{money(credit.current_outstanding, credit.currency_code)}</strong></div>
              </div>
              <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10 }}>
                <small>วงเงินคงเหลือ</small><div><strong>{money(credit.available_credit, credit.currency_code)}</strong></div>
              </div>
            </div>

            {!settling && !settlingAll && !detailReceivable && receivables.length ? (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={beginSettlementAll}
                  disabled={!canCollect}
                  style={{ minHeight: 40, padding: "7px 14px", fontWeight: 800 }}
                >
                  ชำระทั้งหมด {money(payAllTotal, credit.currency_code)}
                </button>
              </div>
            ) : null}

            {settlementMessage ? (
              <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10, marginBottom: 10, fontWeight: 700 }}>
                {settlementMessage}
              </div>
            ) : null}

            {detailReceivable ? (
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 12, display: "grid", gap: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                    <div>
                      <small>บิลต้นทาง</small>
                      <div><strong>{detailReceivable.order_no ?? detailReceivable.receivable_no}</strong></div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <small>ยอดค้าง</small>
                      <div><strong>{money(remainingOf(detailReceivable), detailReceivable.currency_code)}</strong></div>
                    </div>
                  </div>
                  <small>{detailReceivable.branch_name ?? detailReceivable.branch_code ?? "ไม่ระบุสาขา"} · ครบกำหนด {shortDate(detailReceivable.due_date)}</small>
                </div>

                {detailLoading ? <div className="sale-context-empty">กำลังโหลดรายละเอียดบิล…</div> : null}
                {detailError ? (
                  <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 10, fontWeight: 700 }}>
                    {detailError}
                  </div>
                ) : null}

                {detail ? (
                  <>
                    <div style={{ display: "grid", gap: 8, maxHeight: "min(38vh, 320px)", overflowY: "auto" }}>
                      {detail.lines.map((line) => (
                        <div key={line.line_id} style={{ borderBottom: "1px solid #eee8da", paddingBottom: 8, display: "grid", gap: 4 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <div>
                              <strong>{line.item_name}{line.variant_name ? ` · ${line.variant_name}` : ""}</strong>
                              <div><small>{line.quantity} × {money(line.unit_price, detail.order.currency_code)}</small></div>
                            </div>
                            <strong>{money(line.line_total, detail.order.currency_code)}</strong>
                          </div>
                          {line.modifiers.length ? (
                            <div style={{ display: "grid", gap: 2, paddingLeft: 10 }}>
                              {line.modifiers.map((modifier) => (
                                <small key={modifier.modifier_id}>
                                  + {modifier.group_name}: {modifier.option_name}
                                  {modifier.total_price_delta ? ` (${money(modifier.total_price_delta, detail.order.currency_code)})` : ""}
                                </small>
                              ))}
                            </div>
                          ) : null}
                          {line.discount_amount > 0 ? <small>ส่วนลดรายการ -{money(line.discount_amount, detail.order.currency_code)}</small> : null}
                        </div>
                      ))}
                    </div>
                    <div style={{ borderTop: "1px solid #e7e1d2", paddingTop: 8, display: "grid", gap: 4 }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span>ยอดสินค้า</span><strong>{money(detail.order.subtotal_amount, detail.order.currency_code)}</strong></div>
                      {detail.order.modifier_amount ? <div style={{ display: "flex", justifyContent: "space-between" }}><span>ตัวเลือก</span><strong>{money(detail.order.modifier_amount, detail.order.currency_code)}</strong></div> : null}
                      {detail.order.discount_amount ? <div style={{ display: "flex", justifyContent: "space-between" }}><span>ส่วนลด</span><strong>-{money(detail.order.discount_amount, detail.order.currency_code)}</strong></div> : null}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18 }}><strong>ยอดสุทธิ</strong><strong>{money(detail.order.grand_total, detail.order.currency_code)}</strong></div>
                    </div>
                  </>
                ) : null}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={resetDetail}>กลับรายการค้างชำระ</button>
                  <button type="button" onClick={() => beginSettlement(detailReceivable)} disabled={!canCollect}>รับชำระบิลนี้</button>
                </div>
              </div>
            ) : settling || settlingAll ? (
              <div style={{ display: "grid", gap: 12 }}>
                {settlingAll ? (
                  <div style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                      <div>
                        <small>ชำระทั้งหมด</small>
                        <div><strong>{receivables.length} บิล</strong></div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <small>ยอดรวม</small>
                        <div><strong>{money(payAllTotal, credit.currency_code)}</strong></div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gap: 4, maxHeight: 150, overflowY: "auto" }}>
                      {receivables.map((item) => (
                        <div key={item.receivable_id} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <small>{item.order_no ?? item.receivable_no}</small>
                          <small><strong>{money(remainingOf(item), item.currency_code)}</strong></small>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : settling ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <small>บิล</small>
                        <div><strong>{settling.order_no ?? settling.receivable_no}</strong></div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <small>ยอดคงเหลือ</small>
                        <div><strong>{money(remainingOf(settling), settling.currency_code)}</strong></div>
                      </div>
                    </div>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span>ยอดที่รับชำระ</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        max={remainingOf(settling)}
                        value={amountText}
                        disabled={settlementState === "COMMITTING" || settlementState === "SYNCING" || settlementState === "PENDING"}
                        onChange={(event) => {
                          setAmountText(event.target.value);
                          if (isCash) setTenderedText(event.target.value);
                          setSettlementMessage(null);
                        }}
                        style={{ minHeight: 44, border: "1px solid #d8d1c1", borderRadius: 10, padding: "8px 10px", fontSize: 18 }}
                      />
                    </label>
                  </>
                ) : null}

                <div style={{ display: "grid", gap: 7 }}>
                  <span>รับเงินด้วย</span>
                  {paymentMethods.length ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {paymentMethods.map((method) => (
                        <button
                          key={method.payment_method_code}
                          type="button"
                          onClick={() => selectMethod(method)}
                          disabled={settlementState === "COMMITTING" || settlementState === "SYNCING" || settlementState === "PENDING"}
                          style={{
                            minHeight: 40,
                            borderRadius: 10,
                            border: selectedMethodCode === method.payment_method_code ? "2px solid #222" : "1px solid #d8d1c1",
                            padding: "7px 12px",
                            fontWeight: 700,
                          }}
                        >
                          {method.payment_method_name}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div>ยังไม่มีช่องทางรับเงินที่พร้อมใช้</div>
                  )}
                </div>

                {isCash ? (
                  <>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span>รับเงินสดมา</span>
                      <input
                        type="number"
                        min={Number.isFinite(settlementAmount) ? settlementAmount : 0}
                        step="0.01"
                        value={tenderedText}
                        disabled={settlementState === "COMMITTING" || settlementState === "SYNCING" || settlementState === "PENDING"}
                        onChange={(event) => { setTenderedText(event.target.value); setSettlementMessage(null); }}
                        style={{ minHeight: 44, border: "1px solid #d8d1c1", borderRadius: 10, padding: "8px 10px", fontSize: 18 }}
                      />
                    </label>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>เงินทอน</span><strong>{money(localChange, credit.currency_code)}</strong>
                    </div>
                  </>
                ) : selectedMethod?.settlement_mode === "RECEIVING_DESTINATION" ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {destinations.length > 1 ? (
                      <label style={{ display: "grid", gap: 5 }}>
                        <span>ช่องทางรับเงิน</span>
                        <select
                          value={selectedDestination?.destination_id ?? ""}
                          onChange={(event) => setSelectedDestinationId(event.target.value)}
                          disabled={settlementState === "COMMITTING" || settlementState === "SYNCING" || settlementState === "PENDING"}
                          style={{ minHeight: 42, border: "1px solid #d8d1c1", borderRadius: 10, padding: "7px 10px" }}
                        >
                          {destinations.map((destination) => (
                            <option key={destination.destination_id} value={destination.destination_id}>{destination.display_name}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {selectedDestination ? <DestinationSummary destination={selectedDestination} /> : null}
                    <small>กดยืนยันเมื่อได้รับเงินแล้ว</small>
                  </div>
                ) : null}

                {!online ? <div style={{ fontWeight: 700 }}>การรับชำระค้างชำระต้องเชื่อมต่ออินเทอร์เน็ต</div> : null}
                {shift?.shift_status !== "OPEN" ? <div style={{ fontWeight: 700 }}>กรุณาเปิดกะก่อนรับชำระ</div> : null}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={resetSettlement} disabled={settlementState === "COMMITTING" || settlementState === "SYNCING"}>กลับ</button>
                  {settlementState === "PENDING" ? (
                    <button
                      type="button"
                      onClick={() => void (settlementAllIntent ? retrySettlementAll() : retrySettlement())}
                      disabled={!online || (!settlementIntent && !settlementAllIntent)}
                    >
                      ลองยืนยันคำขอเดิมอีกครั้ง
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void (settlingAll ? confirmSettlementAll() : confirmSettlement())}
                      disabled={!canCollect || !selectedMethod || settlementState === "COMMITTING" || settlementState === "SYNCING"}
                    >
                      {settlementState === "COMMITTING" || settlementState === "SYNCING"
                        ? "กำลังยืนยัน…"
                        : settlingAll ? "ยืนยันชำระทั้งหมด" : "ยืนยันรับชำระ"}
                    </button>
                  )}
                </div>
              </div>
            ) : receivables.length ? (
              <div style={{ display: "grid", gap: 8, maxHeight: "min(52vh, 420px)", overflowY: "auto" }}>
                {receivables.map((item) => {
                  const remaining = remainingOf(item);
                  const partial = item.receivable_status === "PARTIALLY_PAID" || Number(item.allocated_amount ?? 0) > 0;
                  const occurredAt = item.sale_occurred_at ?? item.created_at;
                  return (
                    <article key={item.receivable_id} style={{ border: "1px solid #e7e1d2", borderRadius: 12, padding: 12, display: "grid", gap: 7 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                        <div>
                          <strong>{item.order_no ?? item.receivable_no}</strong>
                          <div>
                            <small>
                              {item.branch_name ?? item.branch_code ?? "ไม่ระบุสาขา"} · ขาย {shortDate(item.business_date ?? occurredAt)} · {shortTime(occurredAt)} น.
                            </small>
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <small>{partial ? `ยอดเดิม ${money(item.original_amount, item.currency_code)}` : "ยอดค้าง"}</small>
                          <div><strong>{money(remaining, item.currency_code)}</strong></div>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                        <span>ครบกำหนด <strong>{shortDate(item.due_date)}</strong>{partial ? " · ชำระแล้วบางส่วน" : ""}</span>
                        <div style={{ display: "flex", gap: 7 }}>
                          <button type="button" onClick={() => void loadReceivableDetail(item)}>ดูรายละเอียด</button>
                          <button type="button" onClick={() => beginSettlement(item)} disabled={!canCollect}>รับชำระ</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : count > 0 ? (
              <div className="sale-context-empty">กำลังอัปเดตรายการค้างชำระ…</div>
            ) : (
              <div className="sale-context-empty">สมาชิกคนนี้ไม่มีรายการค้างชำระที่เปิดอยู่</div>
            )}
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
